import { useQueryClient } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { toast } from '@round/creatorbase-ui';
import { useUpdatePoolMutation } from '@round/creatorbase-helpers/api-hooks';

const keys = creatorbaseQueries.pools.keys;

type UpdatePoolInfoContext = { previousPool: creatorbase.Pool | undefined };

export function useUpdatePoolInfo(poolId: number | null | undefined) {
    const queryClient = useQueryClient();
    const detailKey = typeof poolId === 'number' ? keys.detail(poolId) : keys.details();

    const { mutate, isLoading } = useUpdatePoolMutation<UpdatePoolInfoContext>({
        onMutate: async ({ id, ...patch }) => {
            await queryClient.cancelQueries({ queryKey: detailKey });
            const previousPool = queryClient.getQueryData<creatorbase.Pool>(detailKey);
            queryClient.setQueryData<creatorbase.Pool>(detailKey, (previous) =>
                previous ? { ...previous, ...patch } : previous
            );
            return { previousPool };
        },
        onError: (_error, variables, context) => {
            const { previousPool } = context ?? {};
            if (previousPool) {
                // Roll back only the fields this mutation touched, so a still-pending edit to the
                // other field keeps its own optimistic value instead of being clobbered.
                const { id: _id, ...patch } = variables;
                queryClient.setQueryData<creatorbase.Pool>(detailKey, (current) => {
                    if (!current) {
                        return previousPool;
                    }

                    const rolledBack = { ...current };
                    for (const field of Object.keys(patch) as (keyof typeof patch)[]) {
                        // Only roll back if this mutation's optimistic value is still the one shown;
                        // a newer commit to the same field owns the value and must not be clobbered.
                        if (current[field] === patch[field]) {
                            rolledBack[field] = previousPool[field];
                        }
                    }

                    return rolledBack;
                });
            }

            toast.error("Couldn't update pool");
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: detailKey });
            queryClient.invalidateQueries({ queryKey: keys.lists() });
            queryClient.invalidateQueries({ queryKey: keys.infiniteLists() });
        },
    });

    return { update: mutate, isLoading };
}
